import { FC, MouseEvent, useContext, useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { useAppSelector } from 'libraries/redux'
import { CategoriesSelectors } from 'store/category/selectors'
import {
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
  useGetFavoriteMerchantQuery,
} from '../../../entities/Favorites/api/apiFavoritesSlice'

import { SupplierContext } from 'context/SupplierContext'

import { HearthIcon, WishListIcon } from 'shared/assets'
import styles from './ProductCard.module.scss'
import { TProductCardProps } from './types'
import HearthFavoriteIcon from 'shared/assets/icons/Product/HeadFavoriteIcon'

const ProductCard: FC<TProductCardProps> = ({ data, className, pageName, page }) => {
  // ==== Props Destructure ====
  const { id, product_id, name, price, special, thumb, thumb_large, isBestSeller, images } = data

  // ==== Context & Selectors ====
  const { is_supplier } = useContext(SupplierContext)
  const queryParam = is_supplier ? Number(window.config.CUSTOMER_ID) : 0
  const productTypeParam = is_supplier ? 'supplier' : 'buyme'
  const storeInfo = useAppSelector(CategoriesSelectors.storeInfo).data

  // ==== API Hooks ====
  const [addFavorite] = useAddFavoriteMutation()
  const [removeFavorite] = useRemoveFavoriteMutation()
  const { data: favoritesData } = useGetFavoriteMerchantQuery({ merchant_id: queryParam, page })

  // ==== Handlers ====
  const cleanPrice = (priceStr: string): number => parseFloat(priceStr.replace(/[^\d.-]/g, ''))

  // ==== Local State ====
  const isInFavorites = data?.in_wishlist || favoritesData?.data?.some((item: any) => item.id === id || item.id === product_id)
  const [isWishlistAdded, setIsWishlistAdded] = useState(isInFavorites)
  const swiperRef = useRef<SwiperRef>(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  // ==== Image logic ====
  const hasImages = images && images.length > 0
  const imageList = hasImages ? images : []
  const productImage = pageName === 'wishlist' ? thumb_large : isBestSeller ? thumb : thumb

  const discountPercent = special && price ? ((1 - cleanPrice(special) / cleanPrice(price)) * 100).toFixed(0) : null

  const handleCardClick = () => {
    // Сохраняем текущую позицию скролла
    sessionStorage.setItem('catalogScrollPosition', window.scrollY.toString())

    if (window.fbq && storeInfo?.fb_pixel_id) {
      window.fbq('track', 'ViewContent', {
        content_ids: [String(id || product_id)],
        content_type: 'product',
      })
    }
    if (window.ttq && storeInfo?.tt_pixel_id) {
      window.ttq.track('ViewContent', {
        content_id: id || product_id,
        content_type: 'product',
        content_name: name,
        value: cleanPrice(price),
        currency: 'UAH',
      })
    }
    if (window.gtag && storeInfo?.ga_tracking_id) {
      window.gtag('event', 'view_item', {
        currency: 'UAH',
        value: cleanPrice(price),
        items: [
          {
            item_id: String(id || product_id),
            item_name: name,
            price: cleanPrice(price),
          },
        ],
      })
    }
  }

  const handleWishlistClick = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const payload = { product_type: productTypeParam, product_id: id || product_id }

    try {
      if (isWishlistAdded) {
        removeFavorite(payload)
        setIsWishlistAdded(false)
      } else {
        addFavorite(payload)
        setIsWishlistAdded(true)

        if (window.fbq && storeInfo?.fb_pixel_id) {
          window.fbq('track', 'AddToWishlist', {
            content_ids: [id || product_id],
            content_name: name,
            content_type: 'product',
            value: cleanPrice(price),
            currency: 'UAH',
          })
        }
        if (window.ttq && storeInfo?.tt_pixel_id) {
          window.ttq.track('AddToWishlist', {
            content_id: id || product_id,
            content_name: name,
            content_type: 'product',
            value: cleanPrice(price),
            currency: 'UAH',
          })
        }
        if (storeInfo?.ga_tracking_id) {
          ;(window as any).gtag('event', 'add_to_wishlist', {
            items: [
              {
                item_id: String(id || product_id),
                item_name: name,
                currency: 'UAH',
                quantity: 1,
                value: cleanPrice(price),
                price: cleanPrice(price),
              },
            ],
          })
        }
      }
    } catch (error) {
      console.error('Wishlist toggle error:', error)
    }
  }

  useEffect(() => {
    setIsWishlistAdded(isInFavorites)
  }, [isInFavorites])

  return (
      <Link to={`/product/${id || product_id}`} onClick={handleCardClick}>
        <div className={classNames(className, styles.container)}>
          <div className={styles.container__image}>
            <div className={styles.container__image__status}>
              {special && <span className={styles.container__image__status_discount}>-{discountPercent}%</span>}
              {/* {isBestSeller && <span className={styles.container__image__status_bestseller}>Хіт продаж</span>} */}
            </div>

            <div
              onClick={handleWishlistClick}
              className={classNames(styles.container__image_hearth, {
                [styles.container__image_hearth_active]: isWishlistAdded,
              })}
            >
              {isWishlistAdded ? <WishListIcon /> : <HearthFavoriteIcon />}
            </div>

            {hasImages && imageList.length > 0 ? (
                <Swiper
                    ref={swiperRef}
                    modules={[Navigation, Autoplay]}
                    navigation
                    onSlideChange={(swiper:any) => setCurrentSlide(swiper.realIndex)}
                    loop={imageList.length > 1}
                    className={styles.container__image_swiper}
                >
                  {imageList.map((image: any, idx: number) => (
                      <SwiperSlide key={idx}>
                        <div className={styles.container__image_layout}>
                          <img src={image.original} alt={`${name} - ${idx + 1}`} />
                        </div>
                      </SwiperSlide>
                  ))}
                </Swiper>
            ) : (
                <div className={styles.container__image_layout}>
                  <img src={productImage} alt={name} />
                </div>
            )}
          </div>

          {images && Array.isArray(images) && images.length > 1 ? (
            <div className={styles.container__pagination}>
              {images.map((_: any, idx: number) => (
                <span
                  key={idx}
                  className={`${styles.container__pagination_bullet} ${
                    idx === currentSlide ? styles.container__pagination_bullet_active : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    swiperRef.current?.swiper.slideToLoop(idx)
                  }}
                />
              ))}
            </div>
          )  :
              <div className={styles.container__pagination}>
              <span
                  className={`${styles.container__pagination_bullet} ${
                     styles.container__pagination_bullet_active
                  }`}
              />
              </div>
          }

          <div className={styles.container__info}>
            <p className={styles.container__name}>{name}</p>
            <p className={styles.container__price}>
              {special ? special.replace(/\s+(?=\d)/g, ',') : price.replace(/\s+(?=\d)/g, ',')}
            </p>
          </div>
        </div>
      </Link>
  )
}

export default ProductCard